import Card from "components/card";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  createColumnHelper,
  getCoreRowModel,
  flexRender,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { IoEye } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import Pagination from "components/pagination/Pagination";
import Modal from "components/modal/Modal";
import EventsModal from "./EventsModal";
import OrganizationEventService from "services/oraganizationEvent.service";
import Loader from "../../../assets/img/loader.gif";
import Avatar from "react-avatar";
import moment from "moment";
import { FiSearch } from "react-icons/fi";
import Toast from "components/toaster";
import { BsChevronDown } from "react-icons/bs";
import tabCache from "utils/tabCache";

const columnHelper = createColumnHelper();

function rowMenuAnchorFromTriggerRect(rect, openAbove) {
  if (openAbove) {
    return {
      placement: "above",
      triggerTop: rect.top,
      left: rect.left,
      width: rect.width,
    };
  }
  return {
    placement: "below",
    top: rect.bottom,
    left: rect.left,
    width: rect.width,
  };
}

function getRowMenuPortalStyle(anchor) {
  if (!anchor) return {};
  if (anchor.placement === "above") {
    const h = typeof window !== "undefined" ? window.innerHeight : 0;
    return {
      left: anchor.left,
      width: anchor.width,
      bottom: h - anchor.triggerTop + 4,
      top: "auto",
    };
  }
  return {
    top: anchor.top + 4,
    left: anchor.left,
    width: anchor.width,
  };
}

const OrganizationEvents = () => {
  const profileRef = useRef();
  const activateRef = useRef();
  const actionMenuPortalRef = useRef(null);
  const prevSearchTextRef = useRef("");
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [sorting, setSorting] = React.useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(3);
  const [popupOpen, setPopupOpen] = useState(false);
  const [profilePopup, setProfilePopup] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [targetStatus, setTargetStatus] = useState(null);
  // Initialize from cache - show loader only on first load
  const [isLoading, setIsLoading] = useState(false);
  const [id, setId] = useState("");
  const [confirmLoader, setConfirmLoader] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchInputValue, setSearchInputValue] = useState("");

  const [isPageChanging, setIsPageChanging] = useState(false);


  const debounce = (func, delay) => {
    let timeoutId;
    return function (...args) {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(this, args);
      }, delay);
    };
  };

  // Create debounced search handler
  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearchText(value);
    }, 500),
    []
  );

  // Handle search input change
  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchInputValue(value);
    debouncedSearch(value);
  };

  const handleOpenAvtive = async (id, currentStatus, newStatus) => {
    setSelectedUser(currentStatus);
    setTargetStatus(newStatus);
    setId(id);
    setPopupOpen(true);
    document.body.style.overflow = "hidden";
    setActiveDropdown(null);
    setActionMenuAnchor(null);
  };

  const columns = [
    columnHelper.accessor("organization_pic", {
      id: "organization_pic",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Title</p>
      ),
      cell: (info) => {
        const title = info.row.original?.name;
        return (
          <div className="flex items-center gap-3 max-w-[200px]">
            {info.getValue() ? (
              <img
                src={
                  info.getValue()?.includes("http")
                    ? info.getValue()
                    : process.env.REACT_APP_BASE_URL + info.getValue()
                }
                alt="Profile"
                className="!h-8 !w-8 rounded-lg object-cover flex-shrink-0"
              />
            ) : (
              <Avatar name={title} className="!h-8 !w-8 rounded-lg object-cover flex-shrink-0" />
            )}
            <p className="line-clamp-2">{title}</p>
          </div>
        );
      },
    }),
    columnHelper.accessor("description", {
      id: "description",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Description</p>
      ),
      cell: (info) => (
        <div
          className="line-clamp-2 w-[200px] items-center overflow-hidden text-ellipsis whitespace-normal"
          title={info.getValue()}
        >
          {info.getValue() ? info.getValue() : "--"}
          {/* <Progress width="w-[108px]" value={info.getValue()} /> */}
        </div>
      ),
    }),
    columnHelper.accessor("account", {
      id: "account",
      header: () => (
        <p className="text-sm ml-6 font-bold uppercase text-white">Address</p>
      ),
      cell: (info) => {
        const address = info.getValue()?.address;
        const hasAddress = address && address.trim() !== "";
        return (
          <div className="line-clamp-2 w-[200px] flex items-center">
            <p className={`text-sm text-white ${hasAddress ? "ml-4" : "ml-10"}`}>
              {hasAddress ? address : "--"}
            </p>
          </div>
        );
      },
    }),
    columnHelper.accessor("phone", {
      id: "phone",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Phone Number</p>
      ),
      cell: (info) => (
        <div className="flex items-center">
          {info.getValue() ? info.getValue() : "--"}
        </div>
      ),
    }),
    columnHelper.accessor("account", {
      id: "account",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Established</p>
      ),
      cell: (info) => {
        const dateofbirth = info.getValue()?.dateofbirth;
        const hasDate = dateofbirth ? true : false;
        return (
          <div className="flex items-center">
            <p className={`text-sm text-white ${hasDate ? "" : "ml-10"}`}>
              {hasDate
                ? moment.utc(dateofbirth).format("DD/MM/YYYY hh:mm A")
                : "--"}
            </p>
          </div>
        );
      },
    }),
    columnHelper.accessor("createdAt", {
      id: "createdAt",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Requested At</p>
      ),
      cell: (info) => (
        <div className="flex items-center whitespace-nowrap">
          {info.getValue()
            ? moment(info.getValue()).format("DD/MM/YYYY hh:mm A")
            : "--"}
        </div>
      ),
    }),
    columnHelper.accessor("status", {
      id: "status",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Action</p>
      ),
      cell: (info) => {
        const rowId = info.row.original._id;
        const currentStatus = info.getValue();
        const rowCount = info.table.getRowModel().rows.length;
        const rowIndex = info.row.index;
        const hasSearch = searchText.trim().length > 0;
        const isLastRow =
          rowCount > 0 && rowIndex === rowCount - 1;
        let openMenuAbove;
        if (hasSearch && rowCount === 1) {
          openMenuAbove = false;
        } else if (rowCount > 1) {
          openMenuAbove = isLastRow;
        } else {
          openMenuAbove = true;
        }
        const statusText =
          currentStatus === "approved"
            ? "Approved"
            : currentStatus === "pending"
            ? "Pending"
            : "Rejected";
        return (
          <div className="flex gap-3">
            {/* <div
              className="ms-center flex w-fit cursor-pointer rounded-md bg-orangePrimary p-1.5 text-white"
              onClick={() => handleOpen(rowId)}
            >
              {isViewLoading && rowId === id ? (
                <img
                  src={Loader}
                  alt="Loading..."
                  className="mx-auto h-4 w-4"
                />
              ) : (
                <IoEye />
              )}
            </div> */}
            <div className="relative">
              <div
                data-organization-status-trigger
                className={`ms-center w-28 m-0 inline-flex items-center justify-between rounded-md px-3 py-1 text-sm font-bold ${
                  currentStatus === "approved"
                    ? "bg-[#15290E]/55 text-[#38B600]"
                    : currentStatus === "pending"
                    ? "bg-[#FF80001A]/10 text-[rgba(255,128,0,1)]"
                    : "bg-[rgba(255,37,37,0.1)] text-[rgba(255,44,57,1)]"
                } cursor-pointer`}
                onClick={(e) => {
                  if (activeDropdown === rowId) {
                    setActiveDropdown(null);
                    setActionMenuAnchor(null);
                  } else {
                    const r = e.currentTarget.getBoundingClientRect();
                    setActionMenuAnchor(
                      rowMenuAnchorFromTriggerRect(r, openMenuAbove)
                    );
                    setActiveDropdown(rowId);
                  }
                }}
              >
                <span className="text-left flex items-center gap-1.5">
                  {statusText}
                </span>
                <BsChevronDown className="h-2 w-2" style={{ strokeWidth: '2' }} />
              </div>
            </div>
            {/* <div
              className="ms-center m-0 flex h-6 w-28 cursor-pointer items-center justify-center rounded-md bg-orangePrimary px-1.5 text-sm font-bold capitalize text-white"
              onClick={() => handleOpenAvtive(rowId, info.getValue())}
            >
              {info.getValue()}
            </div> */}
          </div>
        );
      },
    }),
  ]; // eslint-disable-next-line

  const [profileData, setProfileData] = useState([]);
  // Initialize from cache if available
  const [data, setData] = useState(tabCache.organizationEvents.data || []);
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  const getAllOrganizationEvents = async (isSearching = false) => {
    const isFirstLoad = tabCache.organizationEvents.firstLoad;
    
    // Show loader on first load or when searching
    if (isFirstLoad || isSearching || isPageChanging) {
      setIsLoading(true);
    }
    
    
    try {
      // const body = {
      //   page: page,
      //   size: pageSize,
      //   search: searchText,
      // };
      const body = {
        pagination: {
          page: page,
          pageSize: pageSize,
          projection: {},
          filters: {},
          sort: {},
        },
        searchtext: encodeURIComponent(searchText),
      };
      const response = await OrganizationEventService.getAllOrganizationEvents(
        body
      );
      const eventsData = response.data.data;
      
      // Update state and cache
      setData(eventsData);
      tabCache.organizationEvents.data = eventsData;
      setTotalPages(Math.ceil(response.data.totalCount / pageSize));
      
      // After first API call, don't show loader next time
      if (isFirstLoad) {
        tabCache.organizationEvents.firstLoad = false;
      }
    } catch (error) {
      Toast("error", error.response.data.message);
      const emptyData = [];
      setData(emptyData);
      tabCache.organizationEvents.data = emptyData;
      if (isFirstLoad) {
        tabCache.organizationEvents.firstLoad = false;
      }
    }finally {
      if (isFirstLoad || isSearching || isPageChanging) {
        setIsLoading(false);
      }
      setIsPageChanging(false); // 👈 STOP pagination loader
    }
    
  };

  useEffect(() => {
    // Check if searchText changed (this indicates a search operation)
    const isSearchChange = prevSearchTextRef.current !== searchText;
    if (isSearchChange) {
      prevSearchTextRef.current = searchText;
    }
    
    // If we have cached data, show it immediately and fetch in background
    if (tabCache.organizationEvents.data) {
      // Data already shown from initial state, just fetch in background
      // Show loader if this is a search operation
      getAllOrganizationEvents(isSearchChange);
    } else {
      // No cached data, fetch and show loader
      getAllOrganizationEvents(false);
    }
    // eslint-disable-next-line
  }, [page, pageSize, searchText]);

  const handlePageChange = (newPage) => {
    setIsPageChanging(true);   // 👈 START loader for pagination
    setPage(newPage);
  };
  
  

  const handleToggleActivation = async () => {
    setConfirmLoader(true);
    const previousStatus = selectedUser;
    const nextStatus = targetStatus;
    setData((prevData) => {
      const updatedData = prevData.map((item) =>
        item._id === id ? { ...item, status: nextStatus } : item
      );
      tabCache.organizationEvents.data = updatedData;
      return updatedData;
    });
    try {
      const body = {
        req_id: id,
        status: nextStatus,
      };
      const response =
        await OrganizationEventService.updateOrganizationEventsDetails(body);
      setPopupOpen(false);
      document.body.style.overflow = "unset";
      setActiveDropdown(null);
      setActionMenuAnchor(null);
      setTargetStatus(null);
      Toast("success", "Event Updated Successfully!");
    } catch (error) {
      setData((prevData) => {
        const revertedData = prevData.map((item) =>
          item._id === id ? { ...item, status: previousStatus } : item
        );
        tabCache.organizationEvents.data = revertedData;
        return revertedData;
      });
      Toast("error", error.response.data.message);
    } finally {
      setConfirmLoader(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setProfilePopup(false);
        document.body.style.overflow = "unset";
      }
      if (activateRef.current && !activateRef.current.contains(event.target)) {
        setPopupOpen(false);
        document.body.style.overflow = "unset";
      }
      if (activeDropdown) {
        const t = event.target;
        const inside =
          actionMenuPortalRef.current?.contains(t) ||
          t.closest("[data-organization-status-trigger]");
        if (!inside) {
          setActiveDropdown(null);
          setActionMenuAnchor(null);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [activeDropdown]);

  useEffect(() => {
    if (!activeDropdown) return;
    const close = () => {
      setActiveDropdown(null);
      setActionMenuAnchor(null);
    };
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [activeDropdown]);

  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  const activeRowForMenu =
    activeDropdown && data.find((r) => r._id === activeDropdown);
  const menuRowStatus = activeRowForMenu?.status;

  return (
    <div className="mt-3 grid h-full grid-cols-1 gap-5">
      <div className="col-span-1 h-full w-full rounded-xl 2xl:col-span-1">
        {/* <ComplexTable
          columnsData={columnsDataComplex}
          tableData={tableDataComplex}
        /> */}

        <Card extra={"w-full h-full px-6 pb-6 overflow-x-auto"}>
          {/* <div className="relative flex items-center justify-between pt-4">
            <div className="text-xl font-bold text-white">Users</div>
          </div> */}

          <div className="mt-4 -ml-2 flex justify-start">
            <div className="flex items-center rounded-full bg-navy-900 py-3 text-white xl:w-[180px]">
              <p className="pl-3 pr-2 text-xl">
                <FiSearch className="h-4 w-4 text-white" />
              </p>
              <input
                type="text"
                placeholder="Search..."
                className="block w-full bg-navy-900 text-sm font-medium max-w-[120px] text-white outline-none placeholder:!text-white sm:w-fit"
                onChange={handleSearchInputChange}
                value={searchInputValue}
              />
            </div>
          </div>

          <div
            className={`table-scroll mt-4 -mx-6 relative ${
              isLoading
                ? "h-[68vh] overflow-hidden"
                : "overflow-x-auto"
            }`}
          >
          {isLoading && (
            <div className="pointer-events-none absolute inset-0 z-40 flex items-center justify-center">
                <img src={Loader} alt="Loading..." className="h-16 w-16" />
              </div>
            )}
            <table
              className={`w-full ${isLoading ? "table-fixed" : ""}`}
              style={
                isLoading
                  ? { tableLayout: "fixed", width: "100%" }
                  : { tableLayout: "auto", minWidth: "100%" }
              }
            >
              <thead className={isLoading ? "hidden" : ""}>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr
                    key={headerGroup.id}
                    className="!border-px !border-gray-400"
                  >
                    {headerGroup.headers.map((header) => {
                      return (
                        <th
                          key={header.id}
                          colSpan={header.colSpan}
                          onClick={header.column.getToggleSortingHandler()}
                          className="cursor-pointer border-b-[1px] border-[#fbfbfb1a] pb-2 pr-4 pt-4 text-start first:pl-6 last:pr-6"
                        >
                          <div className="items-center justify-between text-xs text-gray-200">
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {{
                              asc: "",
                              desc: "",
                            }[header.column.getIsSorted()] ?? null}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody>
                {!isLoading && table.getRowModel().rows.length > 0 ? (
                  table.getRowModel().rows.map((row) => {
                    return (
                      <tr
                        key={row.id}
                        className="group border-b border-[#fbfbfb1a] last:border-none hover:bg-[#8E8E8E]/5 hover:!border-b-0 transition-colors cursor-pointer"
                      >
                        {row.getVisibleCells().map((cell, cellIndex) => {
                          const isFirst = cellIndex === 0;
                          const isLast = cellIndex === row.getVisibleCells().length - 1;
                          return (
                            <td
                              key={cell.id}
                              className={`min-w-[150px] border-white/0 py-3 pr-4 ${isFirst ? 'pl-6' : ''} ${isLast ? 'pr-6' : ''}`}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                ) : !isLoading ? (
                  <tr>
                    <td colSpan={columns.length} className="py-4 text-center">
                      <p className="my-3">Data Not Found</p>
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          {activeDropdown &&
            actionMenuAnchor &&
            activeRowForMenu &&
            createPortal(
              <div
                ref={actionMenuPortalRef}
                className="fixed z-[10000] w-28 rounded-md border border-white/10 bg-navy-800 px-1.5 py-1.5 shadow-[0_0_10px_rgba(255,255,255,0.2)]"
                style={getRowMenuPortalStyle(actionMenuAnchor)}
              >
                {menuRowStatus === "approved" ? (
                  <button
                    type="button"
                    className="block w-full rounded-md px-3 py-1 text-left text-xs font-bold text-white transition-colors hover:bg-white/5"
                    onClick={() =>
                      handleOpenAvtive(
                        activeDropdown,
                        menuRowStatus,
                        "rejected"
                      )
                    }
                  >
                    Reject
                  </button>
                ) : menuRowStatus === "rejected" ? (
                  <button
                    type="button"
                    className="block w-full rounded-md px-3 py-1 text-left text-xs font-bold text-white transition-colors hover:bg-white/5"
                    onClick={() =>
                      handleOpenAvtive(
                        activeDropdown,
                        menuRowStatus,
                        "approved"
                      )
                    }
                  >
                    Approve
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      className="block w-full rounded-md px-3 py-1 text-left text-xs font-bold text-white transition-colors hover:bg-white/5"
                      onClick={() =>
                        handleOpenAvtive(
                          activeDropdown,
                          menuRowStatus,
                          "approved"
                        )
                      }
                    >
                      Approve
                    </button>
                    <div className="my-1 border-t border-white/10" />
                    <button
                      type="button"
                      className="block w-full rounded-md px-3 py-1.5 text-left text-xs font-bold text-white transition-colors hover:bg-white/5"
                      onClick={() =>
                        handleOpenAvtive(
                          activeDropdown,
                          menuRowStatus,
                          "rejected"
                        )
                      }
                    >
                      Reject
                    </button>
                  </>
                )}
              </div>,
              document.body
            )}

          {table.getRowModel().rows.length > 0 && !isLoading && (
            <Pagination
              page={page}
              totalPages={totalPages}
              handlePageChange={handlePageChange}
            />
          )}

          {popupOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
              <div
                className="w-96 rounded-xl bg-navy-800 p-6 shadow-lg"
                ref={activateRef}
              >
                <h2 className="mb-4 text-xl font-bold text-white">
                  Confirm Action
                </h2>
                <p className="mb-6 text-gray-600">
                  Are you sure you want to{" "}
                  {targetStatus === "approved"
                    ? "Approve"
                    : targetStatus === "rejected"
                    ? "Reject"
                    : "set to Pending"}{" "}
                  this event?
                </p>
                <div className="flex justify-end space-x-4">
                  <button
                    onClick={() => {
                      setPopupOpen(false);
                      document.body.style.overflow = "unset";
                    }}
                    className="px-5 py-2.5 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-all duration-200 font-semibold text-sm border border-white/20"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      handleToggleActivation();
                    }}
                    className="w-24 rounded-md bg-orangePrimary px-4 py-2 text-white hover:opacity-90"
                  >
                    {confirmLoader ? (
                      <img
                        src={Loader}
                        alt="Loading..."
                        className="mx-auto h-6 w-6"
                      />
                    ) : (
                      "Confirm"
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* <Modal
            isOpen={popupOpen}
            onClose={() => {
              setPopupOpen(false);
              document.body.style.overflow = "unset";
            }}
            isActive={selectedUser}
            onToggle={handleToggleActivation}
            activateRef={activateRef}
            confirmLoader={confirmLoader}
          /> */}

          <EventsModal
            profileRef={profileRef}
            profileData={profileData}
            isOpen={profilePopup}
            onClose={() => {
              setProfilePopup(false);
              document.body.style.overflow = "unset";
            }}
          />
        </Card>
      </div>
    </div>
  );
};

export default OrganizationEvents;
